import { useForm } from 'react-hook-form';
import type { ReactNode } from 'react';
import type { IntakeField } from '../../engine/types';

interface Props {
  fields: IntakeField[];
  onSubmit: (data: {
    email: string;
    name?: string;
    company?: string;
    role?: string;
    phone?: string;
  }) => void;
  turnstileSlot?: ReactNode;
  submitting?: boolean;
}

interface FormValues {
  email: string;
  name?: string;
  company?: string;
  role?: string;
  phone?: string;
  honeypot?: string;
}

export default function IntakeForm({ fields, onSubmit, turnstileSlot, submitting }: Props) {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>();

  const handle = (data: FormValues) => {
    if (data.honeypot) return; // silent reject
    onSubmit({
      email: data.email,
      name: data.name,
      company: data.company,
      role: data.role,
      phone: data.phone,
    });
  };

  return (
    <form noValidate onSubmit={handleSubmit(handle)} className="space-y-4">
      <div className="hidden" aria-hidden="true">
        <label>
          Leave this blank
          <input tabIndex={-1} autoComplete="off" {...register('honeypot')} />
        </label>
      </div>

      <div>
        <label htmlFor="email" className="block text-sm font-medium">
          Email <span aria-hidden>*</span>
        </label>
        <input
          id="email"
          type="email"
          autoComplete="email"
          aria-invalid={errors.email ? 'true' : 'false'}
          aria-describedby={errors.email ? 'email-err' : undefined}
          {...register('email', {
            required: 'Email is required',
            pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email' },
          })}
          className="mt-1 w-full rounded border border-border p-2"
        />
        {errors.email && (
          <p id="email-err" className="mt-1 text-sm text-danger">
            {errors.email.message}
          </p>
        )}
      </div>

      {fields.map((f) => (
        <div key={f.name}>
          <label htmlFor={f.name} className="block text-sm font-medium">
            {f.label} {f.required && <span aria-hidden>*</span>}
          </label>
          <input
            id={f.name}
            type={f.type ?? 'text'}
            autoComplete={f.name === 'phone' ? 'tel' : f.name === 'company' ? 'organization' : f.name === 'name' ? 'name' : 'off'}
            aria-invalid={errors[f.name as keyof FormValues] ? 'true' : 'false'}
            {...register(
              f.name as keyof FormValues,
              f.required ? { required: `${f.label} is required` } : {},
            )}
            className="mt-1 w-full rounded border border-border p-2"
          />
          {errors[f.name as keyof FormValues] && (
            <p className="mt-1 text-sm text-danger">
              {String(errors[f.name as keyof FormValues]?.message)}
            </p>
          )}
        </div>
      ))}

      {turnstileSlot}

      <button
        type="submit"
        disabled={submitting}
        className="rounded bg-brand px-4 py-2 font-medium text-white disabled:opacity-50"
      >
        {submitting ? 'Sending…' : 'Get my result'}
      </button>
    </form>
  );
}
